import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '@/config/api';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const ImageDropzone = ({ onUploadComplete, propertyId, disabled = false }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const uploadFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((file) =>
      ACCEPTED_TYPES.includes(file.type)
    );

    if (files.length === 0) {
      setError('Please upload JPEG, PNG, WebP, or GIF images.');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      if (propertyId) {
        formData.append('property_id', propertyId);
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/upload`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      const uploadedUrls = (response.data.files || []).map((file) => file.url);
      if (uploadedUrls.length > 0) {
        onUploadComplete(uploadedUrls);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [onUploadComplete, propertyId]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || uploading) return;
    uploadFiles(e.dataTransfer.files);
  }, [disabled, uploading, uploadFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled && !uploading) setDragging(true);
  }, [disabled, uploading]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleFileSelect = (e) => {
    uploadFiles(e.target.files);
    e.target.value = '';
  };

  return (
    <div className="space-y-2">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && !uploading && inputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors
          ${dragging ? 'border-[#8B7355] bg-[#F5F3EF]' : 'border-[#D5CFC5] hover:border-[#8B7355] hover:bg-[#FAFAF8]'}
          ${disabled || uploading ? 'opacity-60 cursor-not-allowed' : ''}
        `}
        data-testid="image-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          multiple
          className="hidden"
          onChange={handleFileSelect}
          disabled={disabled || uploading}
        />

        <div className="flex flex-col items-center gap-3">
          {uploading ? (
            <Loader2 size={32} className="text-[#8B7355] animate-spin" />
          ) : (
            <Upload size={32} className="text-[#8B7355]" />
          )}
          <div>
            <p className="text-sm font-medium text-[#1A1A1A]">
              {uploading ? 'Uploading images...' : 'Drag and drop images here'}
            </p>
            <p className="text-xs text-[#9B9B9B] mt-1">
              or click to browse · JPEG, PNG, WebP, GIF · max 10MB each
            </p>
          </div>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600" data-testid="upload-error">{error}</p>
      )}
    </div>
  );
};

export default ImageDropzone;
